import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, Phone, MessageCircle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import logo from "@/assets/logo-mark-256.webp";
import { COMPANY } from "@/lib/company";
import { getActiveProvince } from "@/provinces/_registry";

export function SiteHeader() {
  const { config } = getActiveProvince();
  const [open, setOpen] = useState(false);
  const nav: { label: string; href: string; route?: boolean }[] = [
    { label: "บริการ", href: "/services", route: true },
    { label: "แคตตาล็อก", href: "/catalog", route: true },
    { label: "เฉพาะกลุ่ม", href: "/for", route: true },
    { label: `พื้นที่${config.nameTh}`, href: "/areas", route: true },
    { label: "ประเทศ", href: "/countries", route: true },
    { label: "ราคา", href: "/pricing", route: true },
    { label: "บทความ", href: "/insights", route: true },
    { label: "เกี่ยวกับเรา", href: "/about", route: true },
    { label: "ติดต่อ", href: "/contact", route: true },
  ];
  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:px-4">
        <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3" aria-label="Thai Notary Law — หน้าแรก">
          <img
            src={logo}
            alt={`Thai Notary Law & Service ${config.nameEn}`}
            className="h-9 w-9 shrink-0 sm:h-11 sm:w-11"
            width="44"
            height="44"
          />
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-extrabold tracking-tight text-primary sm:text-sm">THAI NOTARY LAW</div>
            <div className="truncate text-[9px] font-bold uppercase tracking-[0.14em] text-gold sm:text-[10px] sm:tracking-[0.18em]">{config.nameEn} • Thailand</div>
          </div>
        </Link>
        <nav className="hidden items-center gap-6 text-sm font-medium text-foreground/80 lg:flex" aria-label="เมนูหลัก">
          {nav.map((n) => (
            <Link
              key={n.href}
              to={n.href}
              className="hover:text-primary transition-colors"
              activeProps={{ className: "text-primary font-semibold" }}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-2">
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            aria-label={`โทรหาเรา ${COMPANY.phone}`}
            className="hidden rounded-md bg-gold px-4 py-2 text-sm font-bold text-gold-foreground shadow-sm hover:opacity-90 md:inline-flex"
          >
            📞 {COMPANY.phone}
          </a>
          <a
            href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`ติดต่อทาง LINE ${COMPANY.lineId}`}
            className="hidden rounded-md border border-primary px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground sm:inline-flex"
          >
            LINE {COMPANY.lineId}
          </a>
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            aria-label={`โทรหาเรา ${COMPANY.phone}`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-gold text-gold-foreground shadow-sm md:hidden"
          >
            <Phone className="h-5 w-5" />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="เปิดเมนูหลัก"
                className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-primary lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm overflow-y-auto p-0">
              <SheetHeader className="border-b border-border px-5 py-4 text-left">
                <SheetTitle className="text-base font-extrabold text-primary">เมนูหลัก</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col p-2" aria-label="เมนูมือถือ">
                {nav.map((n) => (
                  <Link
                    key={n.href}
                    to={n.href}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-3.5 text-[15px] font-semibold text-foreground/85 transition-colors hover:bg-muted hover:text-primary"
                    activeProps={{ className: "bg-muted text-primary" }}
                  >
                    {n.label}
                  </Link>
                ))}
              </nav>
              <div className="space-y-2 border-t border-border p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                <a
                  href={`tel:${COMPANY.phoneIntl}`}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gold px-4 py-3.5 text-sm font-extrabold text-gold-foreground"
                >
                  <Phone className="h-4 w-4" /> โทร {COMPANY.phone}
                </a>
                <a
                  href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary px-4 py-3.5 text-sm font-extrabold text-primary"
                >
                  <MessageCircle className="h-4 w-4" /> LINE {COMPANY.lineId}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
